import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import * as qz from 'qz-tray';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { CustomToastsService } from './Services/custom-toasts.service';
import { currentQzCertificateVersion } from './version';

@Injectable({ providedIn: 'root' })
export class QzTrayService {
  private signatureCache = new Map<string, string>();
  private pendingSignRequests = new Map<string, Promise<string>>();
  private connectionPromise: Promise<any> | null = null;
  private isInitialized = false;

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string,
    private toastService: CustomToastsService
  ) {
    this.setupReconnectHandler();
  }

  private async ensureConnection(): Promise<any> {
    if (!this.isInitialized) {
      await this.initQZ();
    }

    if (qz.websocket.isActive()) {
      return Promise.resolve();
    }

    if (!this.connectionPromise) {
      this.connectionPromise = this.connectWithPermissionCheck().catch((err: any) => {
        this.connectionPromise = null;
        console.error('QZ Tray connection failed:', err);
        throw err;
      });
    }

    return this.connectionPromise!;
  }

  // Checks the browser's 'local-network-access' permission (Chrome PNA)
  private async connectWithPermissionCheck(): Promise<void> {
    try {
      const status = await (navigator.permissions as any).query({
        name: 'local-network-access'
      });
      if (status.state === 'denied') {
        const message =
          'Printer access is blocked: the "Local Network Access, Apps on Device" permissions ' +
          'has been denied for this site.';
        console.warn('QZ Tray:', message);
        this.toastService.showQzPermissionModal(message);
        return;
      }
    } catch {
      // Browser does not support querying 'local-network-access' — proceed
    }

    await qz.websocket.connect();
  }

  async initQZ() {
    if (this.isInitialized) {
      return;
    }

    try {
      const publicCertificate = await this.loadQzCertificate();

      qz.security.setCertificatePromise((resolve: (arg0: string) => void, reject: any) => {
        resolve(publicCertificate);
      });

      qz.security.setSignaturePromise(this.createSignaturePromise.bind(this));

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize QZ Tray:', error);
      throw error;
    }
  }

  private createSignaturePromise(toSign: string) {
    return (resolve: (signature: string) => void, reject: (error: any) => void) => {
      if (this.signatureCache.has(toSign)) {
        resolve(this.signatureCache.get(toSign)!);
        return;
      }

      if (this.pendingSignRequests.has(toSign)) {
        this.pendingSignRequests.get(toSign)!.then(resolve).catch(reject);
        return;
      }

      const signPromise = this.http
        .post(`${this.baseUrl}qztray/sign`, { toSign }, { responseType: 'text' })
        .toPromise()
        .then((signature: string | undefined) => {
          if (!signature) {
            throw new Error('Signature request returned undefined');
          }
          this.signatureCache.set(toSign, signature);
          this.pendingSignRequests.delete(toSign);
          return signature;
        })
        .catch((error: any) => {
          this.pendingSignRequests.delete(toSign);
          console.error('Signature request failed:', error);
          throw error;
        });

      this.pendingSignRequests.set(toSign, signPromise);
      signPromise.then(resolve).catch(reject);
    };
  }

  async loadQzCertificate(): Promise<string> {
    const cachedVersion = localStorage.getItem('qz-certificate-version');
    const currentVersion = currentQzCertificateVersion.toString();
    const cachedCert = localStorage.getItem('qz-certificate');

    if (cachedCert && cachedVersion === currentVersion) {
      return cachedCert;
    }

    try {
      const cert = await this.http.get(`${this.baseUrl}qztray/certificate`, { responseType: 'text' }).toPromise();

      if (cert) {
        localStorage.setItem('qz-certificate', cert);
        localStorage.setItem('qz-certificate-version', currentVersion.toString());
      }

      return cert ?? '';
    } catch (err) {
      console.error('Failed to load certificate:', err);
      throw err;
    }
  }

  getPrinters(): Observable<string[]> {
    return from(this.ensureConnection().then(() => qz.printers.find())).pipe(map(printers => printers as string[]));
  }

  getPrinter(printerName: string): Observable<string> {
    return from(this.ensureConnection().then(() => qz.printers.find(printerName))).pipe(map(() => printerName));
  }

  printHTML(printerName: string, htmlData: any) {
    this.ensureConnection()
      .then(() => {
        qz.printers
          .find(printerName)
          .then(() => {
            const config = qz.configs.create(printerName);
            qz.print(config, htmlData).catch((err: any) => console.error('Print failed:', err));
          })
          .catch((err: any) => console.error('Printer not found:', err));
      })
      .catch((err: any) => console.error('Connection failed:', err));
  }

  printImage(printerName: string, imageData: string) {
    this.ensureConnection()
      .then(() => {
        qz.printers
          .find(printerName)
          .then(() => {
            const config = qz.configs.create(printerName);
            const printData = [
              {
                type: 'pixel',
                format: 'image',
                flavor: 'base64',
                data: imageData
              }
            ];

            qz.print(config, printData).catch((err: any) => console.error('Print failed:', err));
          })
          .catch((err: any) => console.error('Printer not found:', err));
      })
      .catch((err: any) => console.error('Connection failed:', err));
  }

  printData(printer: string, data: any): Observable<any> {
    return from(
      this.ensureConnection().then(() => {
        const config = qz.configs.create(printer);
        return qz.print(config, data);
      })
    ).pipe(map(result => result));
  }

  printZpl(printerName: string, zpl: string): Observable<any> {
    return from(
      this.ensureConnection().then(() => {
        const config = qz.configs.create(printerName);
        const printData = [
          {
            type: 'raw',
            format: 'plain',
            data: zpl
          }
        ];
        return qz.print(config, printData);
      })
    ).pipe(map(result => result));
  }

  printDataBatch(printerName: string, printDataArray: any[]): Observable<any> {
    return from(
      this.ensureConnection().then(() => {
        const batchPrintData = printDataArray.map(data => ({
          type: 'raw',
          format: 'plain',
          data: Array.isArray(data) ? data.join('\n') : data
        }));

        const config = qz.configs.create(printerName);
        return qz.print(config, batchPrintData);
      })
    ).pipe(map(result => result));
  }

  disconnect(): void {
    if (qz.websocket.isActive()) {
      qz.websocket.disconnect();
    }
    this.connectionPromise = null;
    this.isInitialized = false;
  }

  clearSignatureCache(): void {
    this.signatureCache.clear();
    this.pendingSignRequests.clear();
  }

  getCacheInfo(): { cacheSize: number; pendingRequests: number } {
    return {
      cacheSize: this.signatureCache.size,
      pendingRequests: this.pendingSignRequests.size
    };
  }

  private setupReconnectHandler() {
    qz.websocket.setClosedCallbacks(() => {
      console.warn('QZ Tray connection closed. Attempting reconnect...');
      this.connectionPromise = null;
      setTimeout(() => {
        this.ensureConnection().catch(err => console.error('Reconnect failed:', err));
      }, 2000);
    });

    qz.websocket.setErrorCallbacks((err: any) => {
      console.error('QZ Tray WebSocket error:', err);
      this.connectionPromise = null;
    });
  }
}
